import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { BankAccount, AccountingCategory, OffsetCode, RecurringActionLine } from '../../types/database';
import { RecurringActionLineInsert } from '../../lib/recurringActionsService';
import { offsetCodesService } from '../../lib/offsetCodesService';
import { emitToast } from '../ui/Toast';

interface EditRecurringActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (updates: Partial<RecurringActionLineInsert>, id: string) => void;
  bankAccounts: BankAccount[];
  accountingCategories: AccountingCategory[];
  isSaving: boolean;
  companyId: string;
  editingLine: RecurringActionLine | null;
}

export function EditRecurringActionModal({
  isOpen,
  onClose,
  onSave,
  bankAccounts,
  accountingCategories,
  isSaving,
  companyId,
  editingLine,
}: EditRecurringActionModalProps) {
  const [offsetCodes, setOffsetCodes] = useState<OffsetCode[]>([]);
  const [loadingOffsets, setLoadingOffsets] = useState(false);
  
  const [formData, setFormData] = useState({
    action_date: '',
    description: '',
    amount: '',
    bank_account_id: '',
    accounting_category_id: '',
    offset_code_id: '',
  });

  // Pre-fill form when editing
  useEffect(() => {
    if (editingLine) {
      setFormData({
        action_date: editingLine.action_date,
        description: editingLine.description,
        amount: editingLine.amount.toString(),
        bank_account_id: editingLine.bank_account_id,
        accounting_category_id: editingLine.accounting_category_id,
        offset_code_id: editingLine.offset_code_id || '',
      });
    }
  }, [editingLine]);

  // Fetch offset codes when modal opens
  useEffect(() => {
    if (isOpen && companyId) {
      loadOffsetCodes();
    }
  }, [isOpen, companyId]);

  const loadOffsetCodes = async () => {
    setLoadingOffsets(true);
    try {
      const { data, error } = await offsetCodesService.getOffsetCodes(companyId);
      if (error) {
        console.error('Error loading offset codes:', error);
      } else if (data) {
        setOffsetCodes(data.filter(code => code.is_active));
      }
    } catch (err) {
      console.error('Error loading offset codes:', err);
    } finally {
      setLoadingOffsets(false);
    }
  };

  if (!isOpen || !editingLine) return null;

  const handleClose = () => {
    onClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required accounting category
    if (!formData.accounting_category_id) {
      emitToast('חובה לבחור סיווג הנהלת חשבונות', 'error');
      return;
    }
    
    const updates: Partial<RecurringActionLineInsert> = {
      action_date: formData.action_date,
      description: formData.description,
      amount: parseFloat(formData.amount),
      bank_account_id: formData.bank_account_id,
      accounting_category_id: formData.accounting_category_id,
      offset_code_id: formData.offset_code_id || null,
    };

    onSave(updates, editingLine.id);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b flex-shrink-0">
            <h2 className="text-xl font-semibold text-gray-900">
              ערוך פעולה קבועה
            </h2>
            <button
              onClick={handleClose}
              disabled={isSaving}
              className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
            <div className="p-6 space-y-6 overflow-y-auto flex-1">
              {/* Base Fields */}
              <div className="space-y-4">
                {/* Action Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
                    תאריך <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.action_date}
                    onChange={(e) => setFormData({ ...formData, action_date: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
                    תיאור <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-right"
                    placeholder="לדוגמה: שכר דירה חודשי"
                    required
                    dir="rtl"
                  />
                </div>

                {/* Amount */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
                    סכום <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-right"
                    placeholder="0.00"
                    required
                  />
                  <p className="mt-1 text-xs text-gray-500 text-right">
                    מספר חיובי להכנסה, שלילי להוצאה
                  </p>
                </div>

                {/* Bank Account */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
                    חשבון בנק <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.bank_account_id}
                    onChange={(e) => setFormData({ ...formData, bank_account_id: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-right"
                    required
                  >
                    <option value="">בחר חשבון בנק</option>
                    {bankAccounts.map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.nickname} - {account.bank_name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Accounting Category */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
                    סיווג הנהלת חשבונות <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.accounting_category_id}
                    onChange={(e) => setFormData({ ...formData, accounting_category_id: e.target.value })}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-right ${
                      !formData.accounting_category_id ? 'border-red-400 bg-red-50' : 'border-gray-300'
                    }`}
                    required
                  >
                    <option value="">בחר סיווג</option>
                    {accountingCategories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Offset Rule */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
                    חוק קיזוז
                  </label>
                  <select
                    value={formData.offset_code_id}
                    onChange={(e) => setFormData({ ...formData, offset_code_id: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-right"
                    disabled={loadingOffsets}
                  >
                    <option value="">ללא קיזוז</option>
                    {offsetCodes.map((code) => (
                      <option key={code.id} value={code.id}>
                        {code.code_number} - {code.name}
                      </option>
                    ))}
                  </select>
                  {loadingOffsets && (
                    <p className="mt-1 text-xs text-gray-500 text-right">
                      טוען חוקי קיזוז...
                    </p>
                  )}
                  <p className="mt-1 text-xs text-gray-500 text-right">
                    אופציונלי - בחר חוק קיזוז לקישור אוטומטי
                  </p>
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50 flex-shrink-0">
              <button
                type="button"
                onClick={handleClose}
                disabled={isSaving}
                className="px-6 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                ביטול
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className="px-6 py-2.5 text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? 'שומר...' : 'עדכן פעולה'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
